import { cva, type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';

import { cn } from '../../../shadcn/lib/utils';

type ProgressBarNodeState = 'done' | 'in-progress' | 'to-do';

type ProgressBarItem = {
  readonly active?: boolean;
  readonly id: string;
  readonly label: string;
  readonly state: ProgressBarNodeState;
  readonly onClick?: () => void;
};

type ProgressBarProps = {
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly items: ProgressBarItem[];
};

const nodeVariants = cva(
  'inline-flex items-center justify-center rounded-full border-2 h-[18px] w-[18px] border-[var(--color-primary-600)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-[var(--color-primary-600)] focus-visible:ring-offset-2 focus-visible:ring-offset-white',
  {
    variants: {
      state: {
        done: `bg-[var(--color-primary-600)] text-white`,
        'in-progress': `bg-[var(--color-primary-600)] text-white`,
        'to-do': `bg-white text-[var(--color-primary-400)]`,
      },
      active: {
        true: `ring-2 ring-[var(--color-primary-600)] ring-offset-2 ring-offset-white`,
      },
    },
    defaultVariants: { state: 'to-do', active: false },
  }
);

type ProgressNodeProps = VariantProps<typeof nodeVariants> & {
  readonly active?: boolean;
  readonly label: string;
  readonly onClick?: () => void;
};

const ProgressLine = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-x-[9px] top-1/2 h-[2px] -translate-y-1/2"
    style={{ backgroundColor: 'var(--color-primary-600)', opacity: 0.9 }}
  />
);

const ProgressNode = ({ state, onClick, label, active }: ProgressNodeProps) => {
  const Element = onClick ? 'button' : 'span';
  return (
    <li className={'relative flex flex-col items-center text-center gap-2'}>
      <Element
        aria-label={label}
        className={cn(nodeVariants({ state, active }), { 'cursor-pointer': Boolean(onClick) })}
        onClick={onClick}
        type={Element === 'button' ? 'button' : undefined}
      >
        {state === 'done' && <Check className="h-3 w-3" color="white" strokeWidth={3} />}
      </Element>
    </li>
  );
};

const ProgressBar = ({ ariaLabel = 'Progress', className, items }: ProgressBarProps) => {
  if (!items.length) {
    return null;
  }

  return (
    <section aria-label={ariaLabel} className={cn('w-full py-3', className)}>
      <ol className="relative flex w-full items-center justify-between">
        <ProgressLine />
        {items.map(({ id, label, state, onClick, active = false }) => (
          <ProgressNode active={active} key={id} label={label} onClick={onClick} state={state} />
        ))}
      </ol>
    </section>
  );
};

export { ProgressBar, ProgressNode };
export type { ProgressBarItem, ProgressBarNodeState };
